import {r as register, l as load} from '@theme/theme-sections';
import {s as sliderLifecycle} from '@theme/section-slider-lifecycle';
import {observeSectionVisibility} from '@theme/section-visibility';

const type = 'product-carousel';
if (!window.Shopify?.theme?.sections?.registered?.[type]) register(type, {
  ...sliderLifecycle,
  onLoad() {
    this.teardown();
    this.sectionActive = true;
    this.sectionVisible = false;
    this.sliderNeedsRefresh = false;
    this.editorial = this.container.dataset.editorialCarousel === 'true';
    this.elements = {
      section: this.container.closest('.shopify-section') || this.container,
      slider: this.container.querySelector('[data-slider]'),
      progressBar: this.container.querySelector('[data-slider-progress-bar]'),
      previous: this.container.querySelector('[data-carousel-previous]'),
      next: this.container.querySelector('[data-carousel-next]'),
      status: this.container.querySelector('[data-carousel-status]'),
      navigation: this.container.querySelector('[data-carousel-navigation]'),
    };
    this.sliderForced = false;
    this.pendingNavigation = [];
    this.pendingCell = null;
    this.requestController = new AbortController();
    const signal = this.requestController.signal;
    const demand = () => { if (!this.slider) this.initSlider({force: true}); };
    this.container.addEventListener('pointerover', demand, {signal, passive: true});
    this.container.addEventListener('pointerdown', demand, {signal, passive: true});
    this.container.addEventListener('focusin', demand, {signal});
    this.elements.slider?.addEventListener('keydown', event => {
      if (this.slider || event.target !== this.elements.slider || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.pendingNavigation.push(slider => slider[event.key === 'ArrowLeft' ? 'previous' : 'next'](false, this.reducedMotion?.matches));
      demand();
    }, {signal});
    this.elements.progressBar?.addEventListener('click', event => {
      if (this.slider) return;
      event.preventDefault();
      const bounds = this.elements.progressBar.getBoundingClientRect();
      if (bounds.width) {
        const ratio = (event.clientX - bounds.left) / bounds.width;
        this.pendingNavigation.push(slider => slider.select(Math.max(0, Math.min(slider.slides.length - 1, Math.floor(ratio * slider.slides.length)))));
      }
      demand();
    }, {signal});
    if (this.editorial) {
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        this.sliderNeedsRefresh = true;
        this.initSlider();
      }, {signal});
      this.elements.previous?.addEventListener('click', () => this.slider?.previous(false, this.reducedMotion.matches), {signal});
      this.elements.next?.addEventListener('click', () => this.slider?.next(false, this.reducedMotion.matches), {signal});
    }
    this.visibilityCleanup = observeSectionVisibility(this.container, visible => {
      if (!this.isSectionActive()) return;
      this.sectionVisible = visible;
      if (visible && (!this.slider || this.sliderNeedsRefresh)) this.initSlider();
    }, {rootMargin: `${Math.min(4000, Math.max(2000, window.innerHeight * 2))}px 0px`});
  },
  initSlider({force = false} = {}) {
    const element = this.elements.slider;
    if (!this.isSectionActive() || !element) return;
    if (!force && (!this.sectionVisible || document.visibilityState === 'hidden')) return;
    this.sliderForced ||= force;
    return this.withSliderDependencies(dependencies => {
      if (!this.sliderForced && (!this.sectionVisible || document.visibilityState === 'hidden')) return;
      this.sliderForced = false;
      this.createSlider(dependencies);
    });
  },
  createSlider({Flickity, SliderProgressIndicator}) {
    const element = this.elements.slider;
    this.destroySlider();
    this.sliderNeedsRefresh = false;
    const desktop = matchMedia(`(min-width: ${window.theme.breakpoints.lg}px)`).matches;
    // Preserve both homepage styles. Edit Page uses the same contained style1
    // carousel, with external controls and editorial card accessibility.
    const styles = {
      style1: {
        prevNextButtons: true, pageDots: false, contain: true, groupCells: true,
        arrowShape: 'M62.5 77.6517L34.8484 50L62.5 22.3484L65.1517 25L40.1517 50L65.1517 75L62.5 77.6517Z',
      },
      style2: {
        prevNextButtons: true, pageDots: false, cellAlign: desktop ? 'left' : 'center',
        contain: true, wrapAround: true, arrowShape: 'M63.5813 87.5L26.0813 50L63.5813 12.5',
      },
    };
    const options = {...styles[this.container.dataset.style]};
    if (this.editorial) Object.assign(options, {
      cellSelector: '[data-carousel-product]', cellAlign: 'left', prevNextButtons: false,
      wrapAround: false, accessibility: true, draggable: true,
      selectedAttraction: this.reducedMotion.matches ? 1 : .025,
      friction: this.reducedMotion.matches ? 1 : .28,
    });
    this.slider = new Flickity(element, options);
    if (this.elements.progressBar) this.progressIndicator = new SliderProgressIndicator({element: this.elements.progressBar, slider: this.slider});
    if (this.editorial) {
      this.slider.on('select', () => this.updateNavigation());
      this.slider.on('settle', () => this.updateNavigation());
      this.slider.on('resize', () => this.updateNavigation());
      if (this.elements.navigation) this.elements.navigation.hidden = !this.slider.cells.length;
      this.updateNavigation();
    }
    if (this.pendingCell && this.container.contains(this.pendingCell)) this.slider.selectCell(this.pendingCell, false, this.reducedMotion.matches);
    this.pendingCell = null;
    for (const navigate of this.pendingNavigation.splice(0)) navigate(this.slider);
    this.scheduleSliderResize();
  },
  updateNavigation() {
    if (!this.slider) return;
    const start = this.slider.selectedSlide?.target || 0;
    const end = start + this.slider.size.innerWidth;
    const visible = [];
    this.slider.cells.forEach((cell, index) => {
      // Containment overlaps groups on an odd final page. Keep every visible
      // card operable, including the overlapping card outside the selected group.
      const inView = cell.x < end - 1 && cell.x + cell.size.width > start + 1;
      cell.element.toggleAttribute('inert', !inView);
      if (inView) { cell.element.removeAttribute('aria-hidden'); visible.push(index + 1); }
      else cell.element.setAttribute('aria-hidden', 'true');
    });
    if (this.elements.previous) this.elements.previous.disabled = this.slider.selectedIndex === 0;
    if (this.elements.next) this.elements.next.disabled = this.slider.selectedIndex >= this.slider.slides.length - 1;
    if (this.elements.status) this.elements.status.textContent = visible.length ? `Products ${visible[0]} to ${visible.at(-1)} of ${this.slider.cells.length}` : '';
  },
  destroySlider() {
    if (this.elements?.navigation) this.elements.navigation.hidden = true;
    this.elements?.slider?.querySelectorAll('[data-carousel-product]').forEach(cell => {
      cell.removeAttribute('inert'); cell.removeAttribute('aria-hidden');
    });
    sliderLifecycle.destroySlider.call(this);
  },
  onSelect() {
    if (!this.slider || this.sliderNeedsRefresh) this.initSlider({force: true});
  },
  onBlockSelect(event) {
    if (!this.editorial) return;
    const cell = event.target.closest('[data-carousel-product]');
    if (cell) {
      if (!this.slider || this.sliderNeedsRefresh) {
        this.pendingNavigation = [];
        this.pendingCell = cell;
        this.initSlider({force: true});
      } else this.slider.selectCell(cell, false, this.reducedMotion.matches);
    }
  },
});
// Initialise the first instance added through the theme editor as well.
load(type);
